import ColorBox from '../../shared/colorBox';
import { ReactComponent as TagsIcon } from '../../../assets/icons/tags-icon.svg';
import { ReactComponent as TrackIcon } from '../../../assets/icons/track-icon.svg';
import { ReactComponent as DivisionIcon } from '../../../assets/icons/account_tree.svg';
import { ReactComponent as DepartmentIcon } from '../../../assets/icons/apartment.svg';
import { ReactComponent as LevelIcon } from '../../../assets/icons/track-level-icon.svg';
import { ReactComponent as EmployeeStatusIcon } from '../../../assets/icons/employee-status.svg';
import { ReactComponent as ActionPlansIcon } from '../../../assets/icons/assignment_dark.svg';
import { ReactComponent as JobTitleIcon } from '../../../assets/icons/job-title-icon.svg';
import { ReactComponent as ReportToIcon } from '../../../assets/icons/reviewer-icon.svg';
import { ReactComponent as RoleIcon } from '../../../assets/icons/badge.svg';
import { ReactComponent as InviteIcon } from '../../../assets/icons/invite_user.svg';
import { ReactComponent as ReinviteIcon } from '../../../assets/icons/reinvite_user.svg';
import { ReactComponent as DeactivateIcon } from '../../../assets/icons/person_off.svg';
import { ReactComponent as ReactivateIcon } from '../../../assets/icons/person_add.svg';
import { ReactComponent as RevokeAccessIcon } from '../../../assets/icons/revoke_user_access.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-outlined.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-dark.svg';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { SYSTEM_ROLES } from '../../../constants/roles';
import { USER_STATUSES, USER_STATUSES_MAP } from '../../../constants/statuses';
import { BULK_ACTION_OPTIONS } from '../../../constants/bulkActionOptions';
import {
  ACTION_PLANS_FILTER,
  REPORT_TO_FILTER,
} from '../../../constants/filters';
import { PARAMS } from '../../../constants/pages';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import { RESET_PASSWORD_URL } from '../../../constants/appConfig';
import {
  USER_EMPLOYMENT_INFORMATION,
  USER_FIELDS,
  ADD_USER_OPTION_KEYS,
  ADD_USER_OPTIONS,
  USER_VALIDATIONS,
  USER_VIEW_OPTIONS,
  USER_BASIC_PERSONAL_INFORMATION,
} from '../../../constants/people';
import {
  isActiveUserView,
  isDeactivatedUserView,
  isExternalUserView,
} from '../../../utility/person';
import {
  isUserProfileAccessibleCheck,
  hasScopedAuthPermissionCheck,
} from '../../../utility/permissions';
import { systemRoleCheck } from '../../../utility/roles';
import {
  isArrayEmpty,
  isArraySubset,
  isObjectEmpty,
  trimString,
  getItemById,
  isItemInList,
} from '../../../utility/helpers';
import {
  getPersonFullName,
  prepareDivisionsWithDepartments,
  prepareTracksWithLevels,
  prepareAllTracksLevels,
} from '../../../utility/uiUtils';
import { validateField, validateFields } from '../../../utility/validation';
import { filterTags } from '../../../utility/tagUtils';

const { REPORT_TO, JOB_TITLE, ROLE, TRACK, LEVEL } =
  USER_EMPLOYMENT_INFORMATION;

const { FIRST_NAME, LAST_NAME, EMAIL, STATUS } =
  USER_BASIC_PERSONAL_INFORMATION;

const { MANUAL, ASSOCIATE, IMPORT } = ADD_USER_OPTION_KEYS;
const { ADD_USERS_TABLE } = FIELD_TYPES;

const CSV_FILE_USER_FIRST_ROW = 6;

const prepareStatusFilters = (labels, shouldRemoveDeactivated = false) => {
  return USER_STATUSES_MAP.reduce((acc, curr) => {
    const isNotSuspended = shouldRemoveDeactivated
      ? curr.id !== USER_STATUSES.DEACTIVATED
      : true;

    return isNotSuspended
      ? [
          ...acc,
          {
            ...curr,
            name: labels[curr.key],
          },
        ]
      : acc;
  }, []);
};

const prepareReportToFilters = labels => {
  return REPORT_TO_FILTER.map(report => {
    return {
      ...report,
      name: labels[report.translationKey],
    };
  });
};

const prepareActionPlansFilters = labels => {
  return ACTION_PLANS_FILTER.map(actionPlanFilter => {
    return {
      ...actionPlanFilter,
      name: labels[actionPlanFilter.translationKey],
    };
  });
};

export const preparePeoplePageFilters = (
  translations,
  jobTitles,
  tracks,
  divisions,
  departments,
  tagCategories,
  userRoles,
  hasActionPlansFilter = false,
  quickFilters = [],
  selectedView = ''
) => {
  const {
    ROLE: ROLE_FILTER,
    STATUS: STATUS_FILTER,
    SCOPE,
    JOB_TITLE: JOB_TITLE_FILTER,
    TAGS,
    TRACK: TRACK_FILTER,
    LEVEL: LEVEL_TRACK_FILTER,
    DIVISION: DIVISION_FILTER,
    DEPARTMENT: DEPARTMENT_FILTER,
    WITHOUT_ACTION_PLANS,
  } = PARAMS;
  const isDeactivatedUsersView = isDeactivatedUserView(selectedView);

  return [
    {
      id: ROLE_FILTER,
      name: translations.filters.labels[ROLE_FILTER],
      icon: RoleIcon,
      items: userRoles,
    },
    ...(isDeactivatedUsersView
      ? []
      : [
          {
            id: STATUS_FILTER,
            name: translations.filters.labels[STATUS_FILTER],
            icon: EmployeeStatusIcon,
            items: prepareStatusFilters(
              translations.userStatuses.statuses,
              !isDeactivatedUsersView
            ),
          },
        ]),
    ...(!isDeactivatedUsersView
      ? [
          {
            id: SCOPE,
            name: translations.filters.labels[SCOPE],
            icon: ReportToIcon,
            isSingleSelect: true,
            items: prepareReportToFilters(translations.reportToFilter),
          },
        ]
      : []),
    {
      id: JOB_TITLE_FILTER,
      name: translations.filters.labels[JOB_TITLE_FILTER],
      icon: JobTitleIcon,
      items: jobTitles,
    },
    {
      id: TRACK_FILTER,
      name: translations.filters.labels.track,
      icon: TrackIcon,
      items: prepareTracksWithLevels(tracks),
    },
    {
      id: LEVEL_TRACK_FILTER,
      name: translations.filters.labels.trackLevel,
      icon: LevelIcon,
      items: prepareAllTracksLevels(tracks),
    },
    {
      id: DIVISION_FILTER,
      name: translations.filters.labels.division,
      icon: DivisionIcon,
      items: prepareDivisionsWithDepartments(divisions),
    },
    {
      id: DEPARTMENT_FILTER,
      name: translations.filters.labels.department,
      icon: DepartmentIcon,
      items: departments,
    },
    ...(hasActionPlansFilter
      ? [
          {
            id: WITHOUT_ACTION_PLANS,
            name: translations.filters.labels.actionPlans,
            icon: ActionPlansIcon,
            isSingleSelect: true,
            items: prepareActionPlansFilters(translations.actionPlansFilters),
          },
        ]
      : []),
    {
      id: TAGS,
      name: translations.filters.labels[TAGS],
      icon: TagsIcon,
      categoryItemsKey: TAGS,
      hasCategoryColorBox: true,
      hasSubmenuHash: true,
      isCategorized: true,
      items: tagCategories,
      categorySearch: filterTags,
    },
    ...(!isArrayEmpty(quickFilters)
      ? quickFilters?.map(filter => {
          return {
            id: filter.name,
            name: filter.name,
            icon: () => {
              return <ColorBox bgColor={filter.color} isSmall />;
            },
            parent: {
              id: TAGS,
            },
            items: filter?.tags,
          };
        }) || {}
      : []),
  ];
};

export const preparePeoplePagePreHeaderActions = (
  checkbox,
  canEdit,
  canManageOnlySelf
) => {
  return [
    ...(canEdit && !canManageOnlySelf
      ? [
          {
            id: 'action-select-all-users',
            title: checkbox,
            rowKey: 'selectUser',
            isHeaderAction: true,
          },
        ]
      : []),
  ];
};

export const preparePeoplePageHeaderActions = (
  labels,
  canEdit,
  hasGlobalEditScope,
  hasGlobalDeleteScope,
  hasCustomEditScope,
  hasCustomDeleteScope,
  canDelete,
  isExternalUsersView,
  isDeactivatedUsersView
) => {
  if (isExternalUsersView) {
    if (
      hasGlobalEditScope ||
      hasGlobalDeleteScope ||
      hasCustomEditScope ||
      hasCustomDeleteScope
    ) {
      return [
        {
          id: 'actions-column',
          title: labels.actions,
          rowKey: 'actions',
          align: 'center',
          isHeaderAction: true,
          rowCellClass: 'actions-dots-menu',
          minWidth: 60,
          maxWidth: 80,
        },
      ];
    }
    return [];
  }
  if (isDeactivatedUsersView) {
    if (hasGlobalEditScope) {
      return [
        {
          id: 'actions-column',
          title: labels.actions,
          rowKey: 'actions',
          align: 'center',
          isHeaderAction: true,
          rowCellClass: 'actions-dots-menu',
          minWidth: 60,
          maxWidth: 80,
        },
      ];
    }
    return [];
  }
  if (canEdit || canDelete) {
    return [
      {
        id: 'actions-column',
        title: labels.actions,
        rowKey: 'actions',
        align: 'center',
        isHeaderAction: true,
        rowCellClass: 'actions-dots-menu',
        minWidth: 60,
        maxWidth: 80,
      },
    ];
  }

  return [];
};

export const preparePeoplePageTableHeaders = (labels, isExternalUsersView) => [
  {
    id: 1,
    title: labels.name,
    rowKey: 'name',
    sortAs: `${FIRST_NAME},id`,
    minWidth: 140,
    maxWidth: '1.5fr',
    headerCellClass: 'name-header-cell',
  },
  {
    id: 2,
    title: labels.email,
    rowKey: 'email',
    sortAs: 'email,id',
    maxWidth: '1fr',
    hasConditionalTooltip: true,
  },
  ...(!isExternalUsersView
    ? [
        {
          id: 3,
          title: labels.jobTitle,
          rowKey: 'position',
          sortAs: 'position__name,id',
          maxWidth: '1fr',
          hasConditionalTooltip: true,
        },
        {
          id: 4,
          title: labels.levelAndTrack,
          rowKey: 'levelAndTrack',
          sortAs: 'level__name,id',
          maxWidth: '1fr',
          hasConditionalTooltip: true,
        },
      ]
    : []),
  {
    id: 5,
    title: labels.roles,
    rowKey: 'role',
    isSortingDisabled: true,
    maxWidth: '1fr',
    hasConditionalTooltip: true,
  },
  ...(!isExternalUsersView
    ? [
        {
          id: 6,
          title: labels.status,
          rowKey: 'status',
          sortAs: 'status,id',
          minWidth: 158,
          maxWidth: '1fr',
        },
      ]
    : []),
];

export const preparePeoplePageTableData = (
  translations,
  auth,
  accessControl,
  users,
  canAdd,
  canEdit,
  canManageOnlySelf,
  isExternalUsersView,
  userStatuses,
  renderUser,
  renderSelectUser,
  renderActions,
  renderUserStatus
) => {
  return users.map(user => {
    const [PRIMARY_ROLE, ADDITIONAL_ROLE] = user.roles || [];
    const isAssociate = systemRoleCheck(user, SYSTEM_ROLES.ASSOCIATE);
    const isCurrentUser = user.id === auth.id;
    const canAccessUserProfile =
      !isAssociate &&
      isUserProfileAccessibleCheck(accessControl, auth.id, user.id);
    const canEditUser = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PROFILES_EDIT,
      accessControl,
      auth.id,
      user.id
    );
    const canDeleteUser = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PROFILES_DELETE,
      accessControl,
      auth.id,
      user.id
    );
    const userStatus = getItemById(userStatuses, user[STATUS]);

    return {
      ...(canEdit && !canManageOnlySelf
        ? { selectUser: renderSelectUser(user) }
        : {}),
      name: renderUser(user, canAccessUserProfile),
      email: user[EMAIL],
      position: user?.[JOB_TITLE]?.name,
      levelAndTrack:
        user[TRACK] && user[LEVEL]
          ? `${user.level.name} - ${user[TRACK].name}`
          : '',
      role: `${translations.systemRoles[PRIMARY_ROLE]}${
        ADDITIONAL_ROLE ? `, ${ADDITIONAL_ROLE}` : ''
      }`,
      id: user.id,
      status: isAssociate ? null : renderUserStatus(userStatus),
      ...(canAccessUserProfile ? { rowLink: `/people/${user.id}` } : {}),
      ...(canEditUser || canDeleteUser
        ? {
            actions: renderActions(
              user,
              userStatus?.id,
              isCurrentUser,
              canEditUser,
              canDeleteUser
            ),
          }
        : {}),
    };
  });
};

export const prepareManagableUsers = (users, auth, accessControl) => {
  return users.reduce((acc, currentUser) => {
    const isAssociate = systemRoleCheck(currentUser, SYSTEM_ROLES.ASSOCIATE);

    if (
      currentUser.id !== auth.id &&
      currentUser[STATUS] !== USER_STATUSES.DEACTIVATED &&
      !isAssociate &&
      hasScopedAuthPermissionCheck(
        PERMISSION_CODES.PROFILES_EDIT,
        accessControl,
        auth.id,
        currentUser.id
      )
    ) {
      return [...acc, currentUser];
    }
    return acc;
  }, []);
};

export const areAllUsersSelectedCheck = (
  checkedUsers,
  users,
  auth,
  accessControl
) => {
  const managableUsers = prepareManagableUsers(users, auth, accessControl);

  return (
    !isArrayEmpty(checkedUsers) &&
    !isArrayEmpty(managableUsers) &&
    isArraySubset(checkedUsers, managableUsers)
  );
};

export const isDisabledUserSelectCheck = (
  users,
  auth,
  accessControl,
  user = null
) => {
  const managableUsers = prepareManagableUsers(users, auth, accessControl);

  return user
    ? !isItemInList(managableUsers, user)
    : !users.some(cUser => isItemInList(managableUsers, cUser));
};

export const prepareBulkActionOptions = labels => {
  return BULK_ACTION_OPTIONS.map(option => {
    return {
      value: option.value,
      label: labels[option.key],
    };
  });
};

export const prepareCheckboxTooltip = (labels, user, auth) => {
  const isAssociate = systemRoleCheck(user, SYSTEM_ROLES.ASSOCIATE);

  if (user.id === auth.id) {
    return labels.currentUserTooltip;
  } else if (user[STATUS] === USER_STATUSES.DEACTIVATED) {
    return labels.inactiveUserTooltip;
  } else if (isAssociate) {
    return labels.externalUser;
  }

  return labels.userNoPermissionSelect;
};

export const isUserSelectedCheck = (userId, selectedUsers) => {
  return selectedUsers.some(checked => checked.id === userId);
};

export const prepareViewDescription = (selectedView, translations) => {
  if (isExternalUserView(selectedView))
    return translations.externalUsersViewDescription;
  if (isDeactivatedUserView(selectedView))
    return translations.deactivatedUsersViewDescription;
  return translations.activeUsersViewDescription;
};

export const prepareWhitelistedFilters = (filters, selectedView) => {
  const isExternalUsersView = isExternalUserView(selectedView);
  const isActiveUsersView = isActiveUserView(selectedView);

  return isExternalUsersView
    ? {}
    : {
        ...filters,
        ...(isActiveUsersView
          ? {}
          : { [PARAMS.STATUS]: [], [PARAMS.SCOPE]: [] }),
      };
};

export const prepareTimezones = () => {
  const timezones = Intl.supportedValuesOf('timeZone');

  return timezones.map(tz => {
    const now = new Date();
    const formattedTimezone = tz?.replace(/_/g, ' ');

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });

    const parts = formatter.formatToParts(now);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';

    return {
      value: formattedTimezone,
      label: `(${offset}) ${formattedTimezone}`,
    };
  });
};

export const prepareInitialExternalUserData = user => ({
  [FIRST_NAME]: user[FIRST_NAME] || '',
  [LAST_NAME]: user[LAST_NAME] || '',
  [EMAIL]: user[EMAIL] || '',
  [ROLE]: user[ROLE],
  [REPORT_TO]: null,
});

export const prepareAddUsersInfo = (labels, selectedOption) => {
  if (selectedOption === MANUAL) {
    return labels.manual;
  }
  if (selectedOption === IMPORT) {
    return labels.import;
  }

  return labels.associate;
};

export const prepareUserViewOptions = labels =>
  USER_VIEW_OPTIONS.map(view => ({
    ...view,
    name: labels[view.key],
  }));

const getNewUser = (selectedOption, organizationMentor) => ({
  [FIRST_NAME]: '',
  [LAST_NAME]: '',
  [EMAIL]: '',
  [ROLE]:
    selectedOption === ASSOCIATE ? SYSTEM_ROLES.ASSOCIATE : SYSTEM_ROLES.USER,
  ...(selectedOption === MANUAL
    ? {
        [REPORT_TO]: [organizationMentor],
        [STATUS]: USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
        activation_url: RESET_PASSWORD_URL,
        client_domain: window.document.location.origin,
      }
    : {}),
});

export const usersValidator = async (
  users,
  columns,
  initialUsers = [],
  rowIndex = -1
) => {
  const errors = {};

  for (let i = 0; i < users.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const rowErrors = await validateFields(
      columns,
      users[i],
      initialUsers[i],
      rowIndex !== -1
    );

    if (!rowErrors[EMAIL]) {
      const isEmailDuplicate = users.some(
        (u, index) =>
          index !== i &&
          trimString(u[EMAIL]) &&
          trimString(u[EMAIL]) === users[i][EMAIL]
      );

      if (isEmailDuplicate) {
        rowErrors[EMAIL] = 'duplicatedEmail';
      }
    }

    if (!isObjectEmpty(rowErrors)) {
      errors[i] = rowErrors;
    }
  }

  return !isObjectEmpty(errors) ? errors : null;
};

export const prepareAddUsersFields = (
  classes,
  descriptionLabels,
  organizationMentor,
  selectedOption,
  roles,
  reportToOptions,
  importedUsers,
  isUserImport,
  onMentorChange
) => {
  let columns = [];
  const reportToInitialValue = getPersonFullName(organizationMentor);

  if (selectedOption === ASSOCIATE) {
    columns = [
      { ...USER_FIELDS.FIRST_NAME },
      { ...USER_FIELDS.LAST_NAME },
      { ...USER_FIELDS.EMAIL },
      { ...USER_FIELDS.ROLE, isDisabled: true },
    ];
  } else {
    columns = [
      { ...USER_FIELDS.FIRST_NAME },
      { ...USER_FIELDS.LAST_NAME },
      { ...USER_FIELDS.EMAIL },
      {
        ...USER_FIELDS.REPORT_TO,
        initialValue: reportToInitialValue,
      },
      { ...USER_FIELDS.ROLE },
      { ...USER_FIELDS.INVITED, ...(isUserImport ? { key: 'invite' } : {}) },
    ];
  }

  return [
    {
      name: 'users',
      type: ADD_USERS_TABLE,
      description: prepareAddUsersInfo(descriptionLabels, selectedOption),
      fieldWrapperClass: classes.addUsersTable,
      userAddType: selectedOption,
      columns,
      roles,
      reportToOptions,
      hasInvite: selectedOption !== ASSOCIATE,
      hasAddButton: selectedOption !== IMPORT,
      hasDelete: selectedOption !== IMPORT,
      organizationUserKey: 'organizationUser',
      getNewUser,
      onMentorChange,
      usersValidator,
      validators: [
        {
          validator: users => usersValidator(users, columns, importedUsers, -1),
          isMultiple: true,
        },
      ],
    },
  ];
};

export const prepareAddUsersInitialValues = (
  selectedOption,
  organizationMentor,
  importedUsers = []
) => {
  return {
    users: [
      ...(isArrayEmpty(importedUsers)
        ? [getNewUser(selectedOption, organizationMentor)]
        : []),
      ...importedUsers.map(user => ({
        ...user,
        [REPORT_TO]: [organizationMentor],
      })),
    ],
  };
};

export const prepareUsersForSave = (users, initialUsers = []) => {
  if (!isArrayEmpty(initialUsers)) {
    return users.reduce((acc, user, index) => {
      const preparedUser = {
        id: user.id,
        [FIRST_NAME]: user[FIRST_NAME],
        [LAST_NAME]: user[LAST_NAME],
        [REPORT_TO]: user[REPORT_TO][0].id,
        [STATUS]: user[STATUS],
        [ROLE]: user[ROLE],
      };

      if (initialUsers[index][EMAIL] !== trimString(user[EMAIL])) {
        preparedUser[EMAIL] = user[EMAIL];
      }

      return [...acc, preparedUser];
    }, []);
  }

  return users.map(user => ({
    ...user,
    ...(user[REPORT_TO] ? { [REPORT_TO]: user[REPORT_TO][0].id } : {}),
  }));
};

export const prepareAddUserOptions = labels =>
  ADD_USER_OPTIONS.map(option => ({
    ...option,
    name: labels[option.key].title,
    description: labels[option.key].description,
  }));

export const prepareValidImportedUsers = async (users, organizationUser) => {
  const uniqueEmails = new Set();
  const validUsers = [];
  const rowsWithErrors = new Set();

  for (let i = 0; i < users.length; i += 1) {
    const { firstName, lastName, email } = users[i];
    const sanitizedFirstName = trimString(firstName);
    const sanitizedLastName = trimString(lastName);
    const sanitizedEmail = trimString(email);
    if (sanitizedFirstName || sanitizedLastName || sanitizedEmail) {
      // eslint-disable-next-line no-await-in-loop
      const firstNameErrors = await validateField(
        USER_FIELDS.FIRST_NAME,
        sanitizedFirstName
      );
      // eslint-disable-next-line no-await-in-loop
      const lastNameErrors = await validateField(
        USER_FIELDS.LAST_NAME,
        sanitizedLastName
      );
      // eslint-disable-next-line no-await-in-loop
      const emailErrors = await validateField(
        {
          ...USER_FIELDS.EMAIL,
          validators: USER_VALIDATIONS[EMAIL].slice(
            0,
            USER_VALIDATIONS[EMAIL].length - 1
          ),
        },
        sanitizedEmail
      );

      if (!rowsWithErrors.has(i + CSV_FILE_USER_FIRST_ROW) && firstNameErrors) {
        rowsWithErrors.add(i + CSV_FILE_USER_FIRST_ROW);
      }

      if (!rowsWithErrors.has(i + CSV_FILE_USER_FIRST_ROW) && lastNameErrors) {
        rowsWithErrors.add(i + CSV_FILE_USER_FIRST_ROW);
      }

      if (!rowsWithErrors.has(i + CSV_FILE_USER_FIRST_ROW) && emailErrors) {
        rowsWithErrors.add(i + CSV_FILE_USER_FIRST_ROW);
      }

      if (!firstNameErrors && !lastNameErrors && !emailErrors) {
        if (!uniqueEmails.has(sanitizedEmail)) {
          uniqueEmails.add(sanitizedEmail);
          validUsers.push({
            [FIRST_NAME]: sanitizedFirstName,
            [LAST_NAME]: sanitizedLastName,
            [EMAIL]: sanitizedEmail,
            [ROLE]: SYSTEM_ROLES.USER,
            [REPORT_TO]: [organizationUser],
            [STATUS]: USER_STATUSES.ACTIVE_WITHOUT_ACCESS,
            activation_url: RESET_PASSWORD_URL,
            client_domain: window.document.location.origin,
          });
        }
      }
    }
  }

  return {
    rowsWithErrors: [...rowsWithErrors],
    validEmails: [...uniqueEmails],
    validUsers,
  };
};

export const prepareMenuItems = (
  labels,
  user,
  userStatus,
  canEditUser,
  canDeleteUser,
  isCurrentUser,
  onEditExternalUserInfo,
  onEditPersonalInfo,
  onEditEmploymentInfo,
  onInvite,
  onReinvite,
  onRevokeAccess,
  onDeactivate,
  onReactivate,
  onDelete
) => {
  const isAssociate = systemRoleCheck(user, SYSTEM_ROLES.ASSOCIATE);

  return [
    {
      type: 'editPersonalInfo',
      label: labels.editPersonalInfo,
      shouldItemRender: () => canEditUser && isAssociate,
      action: () => onEditExternalUserInfo(user),
      icon: EditIcon,
    },
    {
      type: 'editPersonalInfo',
      label: labels.editPersonalInfo,
      shouldItemRender: () =>
        canEditUser && userStatus !== USER_STATUSES.DEACTIVATED && !isAssociate,
      action: () => onEditPersonalInfo(user),
      icon: EditIcon,
    },
    {
      type: 'editEmploymentInfo',
      label: labels.editEmploymentInfo,
      shouldItemRender: () =>
        canEditUser && userStatus !== USER_STATUSES.DEACTIVATED && !isAssociate,
      action: () => onEditEmploymentInfo(user),
      icon: EditIcon,
    },
    {
      type: 'invite',
      label: labels.invite,
      shouldItemRender: () =>
        canEditUser &&
        userStatus === USER_STATUSES.ACTIVE_WITHOUT_ACCESS &&
        !isAssociate,
      action: onInvite(user),
      icon: InviteIcon,
    },
    {
      type: 'reinvite',
      label: labels.reinvite,
      shouldItemRender: () =>
        canEditUser && userStatus === USER_STATUSES.ACTIVE_INVITED,
      action: onReinvite(user),
      icon: ReinviteIcon,
    },
    {
      type: 'revokeAccess',
      label: labels.revokeAccess,
      shouldItemRender: () =>
        canEditUser &&
        !isCurrentUser &&
        (userStatus === USER_STATUSES.ACTIVE_WITH_ACCESS ||
          userStatus === USER_STATUSES.ACTIVE_INVITED),
      action: onRevokeAccess(user),
      icon: RevokeAccessIcon,
    },
    {
      type: 'deactivate',
      label: labels.deactivate,
      shouldItemRender: () =>
        canEditUser &&
        !isCurrentUser &&
        userStatus !== USER_STATUSES.DEACTIVATED &&
        !isAssociate,
      action: onDeactivate(user),
      icon: DeactivateIcon,
    },
    {
      type: 'reactivate',
      label: labels.reactivate,
      shouldItemRender: () =>
        canEditUser && userStatus === USER_STATUSES.DEACTIVATED,
      action: onReactivate(user),
      icon: ReactivateIcon,
    },
    {
      type: 'delete',
      label: labels.delete,
      shouldItemRender: () =>
        canDeleteUser &&
        !isCurrentUser &&
        (userStatus === USER_STATUSES.DEACTIVATED || isAssociate),
      action: onDelete(user),
      icon: DeleteIcon,
    },
  ];
};
